import { useEffect, useState } from "react";
import { getActivityLogs, type ActivityLog, type ActivityLogsFilters } from "@/lib/api/activityLogs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Activity, Users, FileText, UserPlus, UserCog, Clock, Filter } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface ActivityLogProps {
  workspaceId: string;
}

const activityTypeConfig = {
  decision_created: {
    label: "Entscheidung erstellt",
    icon: FileText,
    color: "text-blue-600 bg-blue-50 border-blue-200",
  },
  phase_changed: {
    label: "Phase gewechselt",
    icon: Activity,
    color: "text-purple-600 bg-purple-50 border-purple-200",
  },
  member_joined: {
    label: "Mitglied beigetreten",
    icon: UserPlus,
    color: "text-green-600 bg-green-50 border-green-200",
  },
  role_changed: {
    label: "Rolle geändert",
    icon: UserCog,
    color: "text-orange-600 bg-orange-50 border-orange-200",
  },
};

const timeFilters = [
  { value: "all", label: "Alle Aktivitäten" },
  { value: "today", label: "Heute" },
  { value: "week", label: "Diese Woche" },
  { value: "month", label: "Dieser Monat" },
];

export function ActivityLog({ workspaceId }: ActivityLogProps) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  useEffect(() => {
    loadLogs();
  }, [workspaceId, timeFilter, selectedTypes]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const filters: ActivityLogsFilters = {};

      // Apply time filter
      if (timeFilter !== "all") {
        const now = new Date();
        if (timeFilter === "today") {
          now.setHours(0, 0, 0, 0);
          filters.dateFrom = now.toISOString();
        } else if (timeFilter === "week") {
          now.setDate(now.getDate() - 7);
          filters.dateFrom = now.toISOString();
        } else if (timeFilter === "month") {
          now.setMonth(now.getMonth() - 1);
          filters.dateFrom = now.toISOString();
        }
      }

      // Apply type filter
      if (selectedTypes.length > 0) {
        filters.activityTypes = selectedTypes;
      }

      const data = await getActivityLogs(workspaceId, filters);
      setLogs(data);
    } catch (err) {
      console.error("Error loading activity logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleActivityType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const getActivityMessage = (log: ActivityLog) => {
    const userName = log.profiles?.name || log.profiles?.email || "Unbekannter Benutzer";
    const metadata = log.metadata || {};

    switch (log.activity_type) {
      case "decision_created":
        return `${userName} hat eine neue Entscheidung erstellt: ${metadata.summary || metadata.decision_type || "Neue Entscheidung"}`;
      case "phase_changed":
        return `${userName} hat "${metadata.summary || "Entscheidung"}" von Phase ${metadata.old_phase} zu Phase ${metadata.new_phase} verschoben`;
      case "member_joined":
        return `${userName} ist dem Workspace als ${metadata.role === "admin" ? "Admin" : "Mitglied"} beigetreten`;
      case "role_changed":
        return `${userName} hat die Rolle von ${metadata.target_user_name || "einem Mitglied"} von ${metadata.old_role} zu ${metadata.new_role} geändert`;
      default:
        return `${userName} hat eine Aktivität durchgeführt`;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Aktivitätsprotokoll</CardTitle>
            <CardDescription>
              Alle Aktivitäten in Ihrem Workspace im Überblick
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Aktivitätstypen
                  {selectedTypes.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedTypes.length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-4">
                  <h4 className="font-medium text-sm">Aktivitätstypen filtern</h4>
                  <div className="space-y-3">
                    {Object.entries(activityTypeConfig).map(([type, config]) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={type}
                          checked={selectedTypes.includes(type)}
                          onCheckedChange={() => toggleActivityType(type)}
                        />
                        <Label
                          htmlFor={type}
                          className="text-sm font-normal cursor-pointer flex items-center gap-2"
                        >
                          <config.icon className="h-4 w-4" />
                          {config.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {selectedTypes.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedTypes([])}
                      className="w-full"
                    >
                      Alle Filter zurücksetzen
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <Select value={timeFilter} onValueChange={setTimeFilter}>
              <SelectTrigger className="w-[180px]">
                <Clock className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeFilters.map((filter) => (
                  <SelectItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Keine Aktivitäten gefunden</p>
          </div>
        ) : (
          <div className="space-y-4">
            {logs.map((log) => {
              const config = activityTypeConfig[log.activity_type as keyof typeof activityTypeConfig];
              const Icon = config?.icon || Activity;

              return (
                <div
                  key={log.id}
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className={`p-2 rounded-full border ${config?.color || "text-gray-600 bg-gray-50 border-gray-200"}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{getActivityMessage(log)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(log.created_at), {
                        addSuffix: true,
                        locale: de,
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
